import { Helmet } from '@dr.pogodin/react-helmet';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import PageNotFound from '../common/lib/PageNotFound';
import type { MultiLang } from '../config';
import Functions from '../functions';
import ItemUtil from './lib/ItemUtil';
import styles from './Xoonips.module.css';
import XoonipsAdvancedSearch from './XoonipsAdvancedSearch';
import XoonipsDetailItem from './XoonipsDetailItem';
import XoonipsSearchByAdvancedKeyword from './XoonipsSearchByAdvancedKeyword';
import XoonipsSearchByIndexId from './XoonipsSearchByIndexId';
import XoonipsSearchByItemType from './XoonipsSearchByItemType';
import XoonipsSearchByKeyword from './XoonipsSearchByKeyword';
import XoonipsTop from './XoonipsTop';

interface Props {
    lang: MultiLang;
}

const toId = (value: string | null): number | null =>
    value !== null && /^\d+$/.test(value) ? parseInt(value, 10) : null;

/**
 * The pages keep the addresses of the original XooNIps 4 module
 * (`detail.php?item_id=N`, `list.php?index_id=N`, `search.php?op=...`), so
 * the page is chosen by the script name and its query string.
 */
const XoonipsSearch = (props: Props) => {
    const { lang } = props;
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    query.delete('ml_lang');
    const op = query.get('op') || '';
    query.delete('op');
    switch (op) {
        case 'quick': {
            const { type, keyword } = ItemUtil.getSearchKeywordByQuery(location.search);
            return <XoonipsSearchByKeyword lang={lang} type={type} keyword={keyword} />;
        }
        case 'advanced': {
            const searchItemType = query.get('search_itemtype');
            if (searchItemType !== null) {
                return <XoonipsSearchByItemType lang={lang} itemType={searchItemType} itemSubType="" />;
            }
            if (Array.from(query).length > 0) {
                return <XoonipsSearchByAdvancedKeyword lang={lang} />;
            }
            return <XoonipsAdvancedSearch lang={lang} />;
        }
    }
    return <PageNotFound lang={lang} />;
};

const Xoonips = (props: Props) => {
    const { lang } = props;
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    return (
        <div className={`xoonips ${styles.database}`}>
            <Helmet>
                <title>
                    {Functions.mlang('[en]Database[/en][ja]データベース[/ja]', lang)} - {Functions.siteTitle(lang)}
                </title>
            </Helmet>
            <Routes>
                <Route index element={<XoonipsTop lang={lang} />} />
                <Route path="index.php" element={<Navigate to="/modules/xoonips" replace />} />
                <Route
                    path="detail.php"
                    element={<XoonipsDetailItem lang={lang} id={toId(query.get('item_id'))} doi={query.get('id')} />}
                />
                <Route
                    path="list.php"
                    element={<XoonipsSearchByIndexId lang={lang} id={toId(query.get('index_id'))} />}
                />
                <Route path="search.php" element={<XoonipsSearch lang={lang} />} />
                <Route path="*" element={<PageNotFound lang={lang} />} />
            </Routes>
        </div>
    );
};

export default Xoonips;
